import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/hooks/useTheme';

const navItems = [
  { to: '/', label: '대시보드', short: '홈', icon: 'ri-dashboard-line', end: true },
  { to: '/students', label: '학생관리', short: '학생', icon: 'ri-user-3-line', end: false },
  { to: '/lessons', label: '수업일지', short: '수업일지', icon: 'ri-book-2-line', end: false },
  { to: '/tuition', label: '교습비 수납', short: '수납', icon: 'ri-wallet-3-line', end: false },
  { to: '/exams', label: '시험 일정', short: '시험', icon: 'ri-calendar-event-line', end: false },
];

export default function AppLayout() {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const isAdmin = profile?.role === 'admin';
  const isDark = theme === 'dark';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
      isActive
        ? 'bg-primary-100 text-primary-700'
        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
    }`;

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50 pb-16 md:pb-0">
      <header className="sticky top-0 z-30 w-full border-b border-background-200 bg-background-50/90 backdrop-blur">
        <div className="flex h-16 w-full items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-6">
            <NavLink to="/" className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-book-marked-line text-lg"></i>
              </span>
              <span className="hidden text-base font-bold tracking-tight text-foreground-950 sm:inline">
                과외매니저
              </span>
            </NavLink>
            <nav className="hidden items-center gap-1 md:flex">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                  <i className={item.icon}></i>
                  {item.label}
                </NavLink>
              ))}
              {isAdmin && (
                <NavLink to="/admin/members" className={linkClass}>
                  <i className="ri-shield-user-line"></i>
                  회원관리
                </NavLink>
              )}
            </nav>
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={isDark ? '라이트 모드로 전환' : '다크 모드로 전환'}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-900"
            >
              <i className={isDark ? 'ri-sun-line' : 'ri-moon-line'}></i>
            </button>
            <Link
              to="/settings"
              title="내 정보"
              className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-background-100"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-user-line"></i>
              </span>
              <span className="text-left">
                <p className="text-sm font-semibold text-foreground-900">
                  {profile?.name || profile?.email}
                </p>
                <p className="text-xs text-foreground-500">
                  {isAdmin ? '운영자' : '선생님'}
                </p>
              </span>
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 whitespace-nowrap"
            >
              <i className="ri-logout-box-r-line"></i>
              로그아웃
            </button>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 md:hidden"
            aria-label="메뉴"
          >
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="border-t border-background-200 px-4 py-3 md:hidden">
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={linkClass}
                  onClick={() => setMobileOpen(false)}
                >
                  <i className={item.icon}></i>
                  {item.label}
                </NavLink>
              ))}
              {isAdmin && (
                <NavLink
                  to="/admin/members"
                  className={linkClass}
                  onClick={() => setMobileOpen(false)}
                >
                  <i className="ri-shield-user-line"></i>
                  회원관리
                </NavLink>
              )}
              <NavLink
                to="/settings"
                className={linkClass}
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-user-settings-line"></i>
                내 정보
              </NavLink>
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3">
              <p className="text-sm font-semibold text-foreground-900">
                {profile?.name || profile?.email}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 whitespace-nowrap"
                >
                  <i className={isDark ? 'ri-sun-line' : 'ri-moon-line'}></i>
                  {isDark ? '라이트' : '다크'}
                </button>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 whitespace-nowrap"
                >
                  <i className="ri-logout-box-r-line"></i>
                  로그아웃
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="w-full flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-background-200 bg-secondary-50">
        <div className="flex w-full flex-col items-center justify-between gap-2 px-4 py-5 text-xs text-foreground-500 sm:flex-row md:px-6">
          <p>과외매니저 · 과외 학생 관리 시스템</p>
          <p>선생님과 학부모를 잇는 학습 기록</p>
        </div>
      </footer>

      <nav
        aria-label="빠른 이동"
        className="pb-safe-nav fixed inset-x-0 bottom-0 z-40 border-t border-background-200 bg-background-50/95 backdrop-blur md:hidden"
      >
        <div className="grid grid-cols-5">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2 text-[10px] font-semibold transition-colors ${
                  isActive ? 'text-primary-600' : 'text-foreground-500'
                }`
              }
            >
              <i className={`${item.icon} text-lg`}></i>
              <span className="whitespace-nowrap">{item.short}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}